<script setup>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
  ref,
  watch,
  nextTick,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

const safeTimestampUrl = url => {
  try {
    return timeStampAppendedURL(url);
  } catch {
    return url || '';
  }
};

const audioSrc = ref(safeTimestampUrl(attachment.dataUrl));

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(attachment.duration || 0);
const playbackSpeed = ref(1);
const sourceVersion = ref(0);

const MAX_METADATA_RETRIES = 3;
const retryAttempt = ref(0);
const PLAYABLE_READY_STATE = 2;
let metadataRetryTimer = null;

const clearMetadataRetryTimer = () => {
  if (metadataRetryTimer) {
    clearTimeout(metadataRetryTimer);
    metadataRetryTimer = null;
  }
};

const resetAudioState = () => {
  clearMetadataRetryTimer();
  retryAttempt.value = 0;
  isPlaying.value = false;
  isMuted.value = false;
  currentTime.value = 0;
  duration.value = attachment.duration || 0;
  playbackSpeed.value = 1;
};

const loadAudio = async ({ forceReload = false } = {}) => {
  if (!attachment.dataUrl) return;

  if (forceReload || !audioSrc.value) {
    sourceVersion.value += 1;
    audioSrc.value = safeTimestampUrl(attachment.dataUrl);
  }

  await nextTick();

  if (!audioPlayer.value) return;

  audioPlayer.value.pause();
  audioPlayer.value.currentTime = 0;
  audioPlayer.value.muted = false;
  audioPlayer.value.playbackRate = playbackSpeed.value;
  audioPlayer.value.load();
};

const isPlayerReady = () => {
  return (audioPlayer.value?.readyState || 0) >= PLAYABLE_READY_STATE;
};

const waitForPlayableAudio = async () => {
  if (isPlayerReady()) {
    return true;
  }

  await nextTick();

  const player = audioPlayer.value;
  if (!player) {
    return false;
  }

  if (player.readyState >= PLAYABLE_READY_STATE) {
    return true;
  }

  return new Promise(resolve => {
    let timeoutId = null;

    const cleanup = () => {
      player.removeEventListener('canplay', onPlayable);
      player.removeEventListener('loadeddata', onPlayable);
      player.removeEventListener('error', onError);
      player.removeEventListener('stalled', onError);
      if (timeoutId) clearTimeout(timeoutId);
    };

    const onPlayable = () => {
      cleanup();
      resolve(true);
    };

    const onError = () => {
      cleanup();
      resolve(false);
    };

    timeoutId = setTimeout(() => {
      cleanup();
      resolve(player.readyState >= PLAYABLE_READY_STATE);
    }, 2000);

    player.addEventListener('canplay', onPlayable, { once: true });
    player.addEventListener('loadeddata', onPlayable, { once: true });
    player.addEventListener('error', onError, { once: true });
    player.addEventListener('stalled', onError, { once: true });
  });
};

const syncDurationFromPlayer = () => {
  const nextDuration = audioPlayer.value?.duration;

  if (
    !nextDuration ||
    Number.isNaN(nextDuration) ||
    !Number.isFinite(nextDuration) ||
    nextDuration <= 0
  ) {
    return false;
  }

  clearMetadataRetryTimer();
  duration.value = nextDuration;
  return true;
};

const scheduleMetadataRetry = () => {
  if (!attachment.dataUrl || retryAttempt.value >= MAX_METADATA_RETRIES) {
    return;
  }

  clearMetadataRetryTimer();

  const nextAttempt = retryAttempt.value + 1;
  metadataRetryTimer = setTimeout(() => {
    retryAttempt.value = nextAttempt;
    loadAudio({ forceReload: true });
  }, nextAttempt * 1000);
};

watch(
  () => [attachment.id, attachment.dataUrl],
  ([newId, newUrl], [oldId, oldUrl] = []) => {
    if (!newUrl || (newId === oldId && newUrl === oldUrl)) {
      return;
    }

    resetAudioState();
    loadAudio({ forceReload: true });
  }
);

// When the backend broadcasts MESSAGE_UPDATED with a calculated duration,
// the attachment prop updates in-place (same component instance, same key).
// The duration ref must be synced explicitly since it's not a computed value.
watch(
  () => attachment.duration,
  newDuration => {
    if (newDuration > 0 && duration.value <= 0) {
      duration.value = newDuration;
    }

    if (newDuration > 0 && !isPlayerReady()) {
      loadAudio({ forceReload: true });
    }
  }
);

const { uid } = getCurrentInstance();

const onLoadedMetadata = () => {
  if (!syncDurationFromPlayer()) {
    scheduleMetadataRetry();
  }
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  if (audioSrc.value) {
    loadAudio();
  }
});

onBeforeUnmount(() => {
  clearMetadataRetryTimer();
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const onCanPlay = () => {
  if (!syncDurationFromPlayer()) {
    scheduleMetadataRetry();
  }
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = async () => {
  if (!audioPlayer.value) return;

  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);

    if (!isPlayerReady()) {
      await loadAudio({ forceReload: true });
    }

    const isPlayable = await waitForPlayableAudio();
    if (!isPlayable) {
      isPlaying.value = false;
      scheduleMetadataRetry();
      return;
    }

    try {
      await audioPlayer.value.play();
      isPlaying.value = true;
    } catch {
      isPlaying.value = false;
      scheduleMetadataRetry();
    }
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const onAudioLoadError = () => {
  scheduleMetadataRetry();
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    :key="`${attachment.id}-${sourceVersion}`"
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    preload="metadata"
    @loadedmetadata="onLoadedMetadata"
    @loadeddata="onCanPlay"
    @canplay="onCanPlay"
    @stalled="onAudioLoadError"
    @error="onAudioLoadError"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  >
    <source v-if="audioSrc" :src="audioSrc" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div class="flex gap-1 w-full flex-1 items-center justify-start">
      <button class="p-0 border-0 size-8" @click="playOrPause">
        <Icon
          v-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div
      v-if="attachment.transcribedText"
      class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
    >
      {{ attachment.transcribedText }}
    </div>
  </div>
</template>
